<template>
  <div class="date-input" :class="getUniqueClass">
    <v-menu
      ref="dateMenu"
      v-model="dateMenu"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-y
      offset-overflow
      min-width="auto"
      nudge-bottom="10"
      :position-y="scroll.offset"
    >
      <template v-slot:activator="{ on, attrs }">
        <textInput
          class="input-field"
          :disabled="disabled"
          :value="formatForDatePicker"
          v-bind="attrs"
          v-on="on"
          v-on:click:prepend-inner="dateMenu = true"
          :placeholder="'MM/DD/YYYY'"
          :label="label"
          prepend-inner-icon="mdi-calendar-blank-outline"
          @change="[setConfigValue($event)]"
          ref="input"
          :rules="validationRules"
        >
          <template
            v-if="!hideTooltip && !hideLabel && (!!tooltip || !!helpContent)"
            #append-label
          >
            <tooltip v-if="tooltip" :text="tooltip" />
            <helpIcon v-if="helpContent" :content="helpContent" />
          </template>
        </textInput>
        <template
          v-if="!hideTooltip && hideLabel && (!!tooltip || !!helpContent)"
        >
          <tooltip v-if="tooltip" :text="tooltip" />
          <helpIcon v-if="helpContent" :content="helpContent" />
        </template>
      </template>
      <v-date-picker
        class="qvoDatePicker"
        v-model="date"
        color="#D4D4D4"
        no-title
        reactive
        @input="[setConfigValueFromDatePicker($event)]"
        :event-color="'white'"
        :events="functionEvents"
      >
        <v-spacer />
        <qvoButton text @click="clearDate">Clear</qvoButton>
        <qvoButton @click="dateMenu = false">Done</qvoButton>
      </v-date-picker>
    </v-menu>
  </div>
</template>

<script>
/**
 * @fileoverview Config input field for dates
 *
 * @author Cameron Bulock <cameron.bulock@quavo.com>
 */

import dayjs from "dayjs";
import textInput from "@/qux-common/qux22/components/molecules/input";
import fieldMap from "./field-map.json";
import qvoButton from "@/qux-common/qux22/components/molecules/button";
import getValidationRules from "@/util/getValidationRules";

export default {
  name: "DateField",
  components: { textInput, qvoButton },
  data() {
    return {
      date: "",
      dateMenu: false,
      scroll: {
        parent: null,
        start: null,
        offset: null,
      },
    };
  },
  props: {
    category: {
      type: String,
    },
    disabled: {
      type: Boolean,
    },
    helpContent: {
      type: String,
    },
    hideLabel: {
      type: Boolean,
    },
    id: {
      type: String,
    },
    label: {
      type: String,
    },
    rules: {
      type: Array,
    },
    tooltip: {
      type: String,
    },
    value: {
      type: String,
    },
    parentField: {
      type: String,
    },
  },
  methods: {
    clearDate() {
      this.date = null;
      this.setConfigValue("");
    },
    setConfigValue(value) {
      this.$emit("setConfigValue", value.trim());
    },
    setConfigValueFromDatePicker(value) {
      value = value ? dayjs(value).format("MM/DD/YYYY") : null;
      this.$emit("setConfigValue", value);
    },
    functionEvents(date) {
      return dayjs(date).isBefore(dayjs().format("YYYY-MM-DD"));
    },
    onScroll() {
      this.scroll.offset = this.scroll.start - this.scroll.parent.scrollTop;
    },
    getScrollParent() {
      const node = document.querySelector(`.${this.getUniqueClass}`);
      // based on https://stackoverflow.com/a/49186677 (though updated to only be concerned with y-direction scrolling)
      const regex = /(auto|scroll)/;
      const parents = (_node, ps) => {
        if (_node.parentNode === null) {
          return ps;
        }
        return parents(_node.parentNode, ps.concat([_node]));
      };

      const style = (_node, prop) =>
        getComputedStyle(_node, null).getPropertyValue(prop);
      const overflow = (_node) => style(_node, "overflow-y");
      const scroll = (_node) => regex.test(overflow(_node));

      const scrollParent = (_node) => {
        if (!(_node instanceof HTMLElement || _node instanceof SVGElement)) {
          return;
        }

        const ps = parents(_node.parentNode, []);

        for (let i = 0; i < ps.length; i += 1) {
          if (scroll(ps[i])) {
            return ps[i];
          }
        }

        return document.scrollingElement || document.documentElement;
      };

      return scrollParent(node);
    },
    validate() {
      return this.$refs.input.validate();
    },
  },
  computed: {
    getUniqueClass() {
      // need a random id for each instance of this component
      return `dropdown-${Math.floor(Math.random() * 100000)}`;
    },
    formatForDatePicker() {
      if (this.date) {
        return this.date ? dayjs(this.date).format("MM/DD/YYYY") : null;
      } else {
        return this.value ? dayjs(this.value).format("MM/DD/YYYY") : null;
      }
    },
    getLabel() {
      return (
        (fieldMap[this.category] &&
          fieldMap[this.category][this.id] &&
          fieldMap[this.category][this.id].label) ||
        this.id
      );
    },
    validationRules() {
      return getValidationRules({
        rules: this.rules,
      });
    },
  },
  mounted() {
    // watch workaround for tracking $refs changes
    this.$watch(
      () => {
        return this.$refs.dateMenu.isActive;
      },
      (isActive) => {
        if (isActive) {
          this.scroll.parent = this.getScrollParent();
          this.scroll.parent.addEventListener("scroll", this.onScroll);
          this.scroll.start = this.scroll.parent.scrollTop;
        } else {
          this.scroll.parent.removeEventListener("scroll", this.onScroll);
          this.scroll = {
            parent: null,
            start: null,
            offset: null,
          };
        }
      }
    );
  },
};
</script>

<style lang="scss" scoped>
#app .date-input {
  display: flex;
  gap: $qvo-spacing-8;
  .input-field {
    width: 100%;
  }
  &::v-deep {
    .v-input {
      padding-top: 0;
      width: 11em;
    }
    .v-input__append-inner {
      margin-right: 4px;
    }
  }
}
#app .qvoDatePicker {
  &::v-deep {
    .v-date-picker-header__value {
      order: -1;
    }
    .v-date-picker-header__value button {
      display: flex;
      align-items: center;
      &::after {
        content: "\F0140";
        font: normal normal normal 24px/1 "Material Design Icons";
      }
    }
    .v-date-picker-table {
      height: auto;
    }

    .v-date-picker-table th {
      color: $qvo-color-grey-600;
    }
    .v-date-picker-table button.v-btn--rounded,
    .v-date-picker-table button.v-date-picker-table__current div,
    .v-date-picker-table button.v-btn--active div {
      color: $qvo-color-grey-900;
    }
    .v-date-picker-table button.v-btn--rounded div.v-date-picker-table__events {
      display: none;
    }
    .v-date-picker-table
      button.v-btn--rounded:has(div.v-date-picker-table__events) {
      color: $qvo-color-grey-400;
    }
  }
}
</style>